import React, { useMemo } from 'react'
import { hapticTick } from '../../../../../_utils/haptics'
import { POST_TYPES } from '../../social/data/postTypes'
import { journeyResults, frontDeskResults, voucherResults } from '../data/results'
import { Card, Empty, Pending, plural, moneyOf } from './parts'

const Mini = ({ label, value, sub, pending }) => (
  <div className={`pf-mini${value == null ? ' is-pending' : ''}`}>
    <small>{label}</small>
    {value === undefined ? <span className="pf-skel" aria-label="Loading" /> : <b>{value == null ? '·' : value}</b>}
    {value === null && pending ? <span>{pending}</span> : sub ? <span>{sub}</span> : null}
  </div>
)
const typeLabel = t => (POST_TYPES[t] && POST_TYPES[t].label) || 'Post'

// Tracked booking links (?src=instagram, ?src=qr) from the overview: landings
// (a brand page counts once, not again at the location they pick), the
// location they chose, and bookings by Beu's rule. undefined while loading,
// null when the overview couldn't be read.
export const linkResults = (overview) => {
  if (overview === undefined) return undefined
  if (!overview || !Array.isArray(overview.bySource)) return null
  const names = new Map((overview.byLocation || []).map(l => [String(l.salonId), l.salonName]))
  const rows = overview.bySource.filter(r => r.kind === 'link').map(r => ({
    src: r.id,
    visits: Number(r.visits) || 0,
    chose: Number(r.chosenLocation) || 0,
    fromBrand: Number(r.brandVisits) || 0,
    bookings: Number(r.bookings) || 0,
    value: (Number(r.wentAheadValue) || 0) + (Number(r.upcomingValue) || 0),
    newClients: Number(r.newClients) || 0,
    locations: (r.locations || []).filter(l => l.visits || l.chosen).map(l => ({ ...l, name: names.get(String(l.salonId)) || null })),
  })).sort((a, b) => b.bookings - a.bookings || b.visits - a.visits)
  return { rows, visits: rows.reduce((n, r) => n + r.visits, 0), bookings: rows.reduce((n, r) => n + r.bookings, 0) }
}

const LinksCard = ({ links, money }) => {
  if (!links || !links.rows.length) return null
  const showChose = links.rows.some(r => r.fromBrand || r.chose)
  return (
    <Card title="Tracked links">
      <div className={`pf-tr pf-th pf-t-links${showChose ? '' : ' is-4'}`}><span>Link</span><span className="r">Visits</span>{showChose && <span className="r">Chose a location</span>}<span className="r">Bookings</span><span className="r">Brought in</span></div>
      {links.rows.map(r => (
        <div key={r.src}>
          <div className={`pf-tr pf-t-links${showChose ? '' : ' is-4'}`}><span>?src={r.src}</span><span className="r">{r.visits.toLocaleString('en-GB')}</span>{showChose && <span className="r">{r.fromBrand ? r.chose.toLocaleString('en-GB') : '·'}</span>}<span className="r">{r.bookings.toLocaleString('en-GB')}</span><span className="r">{r.value ? money(r.value) : '·'}</span></div>
          {r.locations.length > 1 && <p className="pf-muted pf-sub">{r.locations.map(l => `${l.name || 'A location'}: ${(l.visits + l.chose).toLocaleString('en-GB')}`).join(' · ')}</p>}
        </div>
      ))}
    </Card>
  )
}

// ─── Social: posting, and whether it works ───
export const SocialTab = ({ data, history, currency = '£' }) => {
  const { social, freeTimes, posts } = data
  const go = (path, state) => { hapticTick(); history.push(path, state) }
  const links = useMemo(() => linkResults(data.overview), [data.overview])
  const money = moneyOf(currency)
  if (!(posts || []).length) {
    return (
      <div className="pf-tab">
        <LinksCard links={links} money={money} />
        <Card title="Social">
          <Empty title="Nothing shared yet" action={<button type="button" className="pf-btn" onClick={() => go('/marketing-social')}>Go to Social</button>}>
            Posts and Stories you share from Social show here: how often you post, what you post, and the free times that got booked.
          </Empty>
        </Card>
      </div>
    )
  }
  const types = Object.keys(social.byType).sort((a, b) => social.byType[b] - social.byType[a])
  return (
    <div className="pf-tab">
      <div className="pf-minis">
        <Mini label="Shared" value={social.shared.toLocaleString('en-GB')} sub={social.due ? `${social.sharedOfDue} of the ${social.due} you planned` : 'Posts and Stories you marked as shared'} />
        <Mini label="Weeks in a row" value={social.streak.toLocaleString('en-GB')} sub={social.streak ? 'With at least one post' : 'Share a post this week to start a run'} />
        <Mini label="Free times booked" value={freeTimes === undefined ? undefined : freeTimes ? `${freeTimes.booked} of ${freeTimes.posted}` : null} sub="Times you posted that were booked afterwards" pending={social.freeTimes.length ? 'The diary couldn’t be read just now' : 'Shows the day after a free times post you share'} />
        <Mini label="From your link in bio" value={links === undefined ? undefined : links && links.rows.length ? plural(links.bookings, 'booking', 'bookings') : null} sub={links && links.rows.length ? `${links.visits.toLocaleString('en-GB')} visits to your tracked links` : null} pending={links === null ? 'Couldn’t be read just now' : 'Add ?src=instagram to the booking link in your bio'} />
      </div>
      <div className="pf-row2">
        <Card title="What you shared">
          {types.length ? (
            <div className="pf-jobs">{types.map(t => <span key={t}>{typeLabel(t)}<b>{social.byType[t]}</b></span>)}</div>
          ) : <p className="pf-muted">Nothing shared in these dates.</p>}
        </Card>
        <Card title="Weeks with a post">
          <div className="pf-weeks">{social.weeks.map(w => <span key={w.start} className={w.posted ? 'is-on' : ''} title={w.posted ? `Posted in the week of ${w.label}` : `Nothing in the week of ${w.label}`}>{w.label}</span>)}</div>
        </Card>
      </div>
      <LinksCard links={links} money={money} />
      <p className="pf-rule">Shared is what you mark in the share sheet, until Instagram can be read directly. Free times booked checks the diary for each time a free times post showed, so it says they were booked after posting, not that the post did it. Posts are kept on this device for now.</p>
    </div>
  )
}

// ─── Journeys and reminders: everything that sends on its own ───
const OPEN_SECTION = { reminder: '_reminders', bookingUpdate: '_updates', salesUpdate: '_sales', reviewBooster: '_reviewbooster', customUpdate: '_custom' }
export const JourneysTab = ({ data, history }) => {
  const { journeys, automations, range } = data
  const rows = useMemo(() => (journeys ? journeyResults(journeys.list, journeys.runs, range).filter(j => j.live || j.entered || j.active) : []), [journeys, range])
  const go = (path, state) => { hapticTick(); history.push(path, state) }
  return (
    <div className="pf-tab">
      <Card title="Journeys">
        {journeys === undefined ? <span className="pf-skel is-list" /> : !journeys ? (
          <Pending>Journeys couldn’t be read just now.</Pending>
        ) : rows.length ? (
          <div className="pf-scroll">
            <div className="pf-table">
              <div className="pf-tr pf-th pf-t-jour"><span>Journey</span><span className="r">Clients in</span><span className="r">In progress</span><span className="r">Booked</span><span className="r">Messages</span></div>
              {rows.map(j => (
                <button key={j.id} type="button" className="pf-tr pf-t-jour" onClick={() => go('/marketing-journeys', { openJourneyId: j.id })}>
                  <span className="pf-name"><b>{j.name}</b>{!j.live && <small>Paused</small>}</span>
                  <span className="r">{j.entered.toLocaleString('en-GB')}</span>
                  <span className="r">{j.active.toLocaleString('en-GB')}</span>
                  <span className={`r${j.booked ? ' pf-pos' : ''}`}>{j.booked ? j.booked.toLocaleString('en-GB') : '·'}</span>
                  <span className="r">{j.messages.toLocaleString('en-GB')}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <Empty title="No journeys running" action={<button type="button" className="pf-btn" onClick={() => go('/marketing-journeys')}>Set up a journey</button>}>
            A journey follows each client, like a welcome email with a rebooking nudge two weeks later. Who went in and who booked shows here.
          </Empty>
        )}
        {rows.length > 0 && <p className="pf-foot">For these dates: clients who started each journey, the bookings it led to and the messages it sent. In progress is everyone in it now.</p>}
      </Card>

      <Card title="Reminders and automations">
        {automations === undefined ? <span className="pf-skel is-list" /> : automations && automations.automations.length ? (
          <div className="pf-scroll">
            <div className="pf-table">
              <div className="pf-tr pf-th pf-t-auto"><span>Automation</span><span className="r">Emails</span><span className="r">Texts</span><span className="r">Tapped</span><span className="r">Led to</span></div>
              {automations.automations.map((a, i) => (
                <button key={a.type || i} type="button" className="pf-tr pf-t-auto" onClick={() => go('/marketing', { openSection: OPEN_SECTION[a.type] || '_custom' })}>
                  <span className="pf-name"><b>{a.name || a.type}</b></span>
                  <span className="r">{(Number(a.emailSent) || 0).toLocaleString('en-GB')}</span>
                  <span className="r">{(Number(a.smsSent) || 0).toLocaleString('en-GB')}</span>
                  <span className="r">{a.clicked ? Number(a.clicked).toLocaleString('en-GB') : '·'}</span>
                  <span className={`r${a.conversions ? ' pf-pos' : ''}`}>{a.conversions ? `${a.conversions} ${a.type === 'reviewBooster' ? (a.conversions === 1 ? 'review' : 'reviews') : (a.conversions === 1 ? 'booking' : 'bookings')}` : '·'}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <Pending action={<button type="button" className="pf-link" onClick={() => go('/marketing')}>Open Communications</button>}>
            Reminders, booking updates and the review booster show what they sent and what they led to once the server reports it.
          </Pending>
        )}
      </Card>
    </div>
  )
}

// ─── Loyalty and gift vouchers ───
export const LoyaltyTab = ({ data, history, currency }) => {
  const { loyalty, vouchers: dash } = data
  const money = moneyOf(currency)
  const v = voucherResults(dash)
  const s = loyalty && loyalty.stats
  const stamps = loyalty && loyalty.scheme && loyalty.scheme.mode === 'stamps'
  const go = (path, state) => { hapticTick(); history.push(path, state) }
  return (
    <div className="pf-tab">
      <div className="pf-row2">
        <Card title="Loyalty" aside={loyalty && loyalty.scheme ? <button type="button" className="pf-link" onClick={() => go('/marketing-loyalty')}>Open Loyalty</button> : null}>
          {loyalty === undefined ? <span className="pf-skel is-list" /> : !loyalty || !loyalty.scheme ? (
            <Empty title="No loyalty scheme yet" action={<button type="button" className="pf-btn" onClick={() => go('/marketing-loyalty')}>Set up loyalty</button>}>
              Points or stamps that bring clients back. Members, rewards and their visits show here.
            </Empty>
          ) : s ? (
            <>
              <div className="pf-minis is-two">
                <Mini label="Members" value={(Number(s.activeCards) || 0).toLocaleString('en-GB')} sub="With a card that’s earning" />
                <Mini label="Visits from members" value={(Number(s.memberVisits) || 0).toLocaleString('en-GB')} sub="As loyalty counts them" />
                <Mini label={stamps ? 'Stamps this month' : 'Points this month'} value={(Number(s.earnedThisMonth) || 0).toLocaleString('en-GB')} sub="Across every member" />
                <Mini label="Rewards redeemed" value={(Number(s.rewardsRedeemed) || 0).toLocaleString('en-GB')} sub="At the till" />
              </div>
              <p className="pf-foot">This month so far. Loyalty doesn’t follow the dates above yet.</p>
            </>
          ) : (
            <Pending>Clients are already earning on their cards. Members, rewards and visits show here as soon as loyalty reporting is connected.</Pending>
          )}
        </Card>
        <Card title="Gift vouchers" aside={<button type="button" className="pf-link" onClick={() => go('/voucher-v2', { subNav: 'sales' })}>See all sales</button>}>
          {dash === undefined ? <span className="pf-skel is-list" /> : v ? (
            <div className="pf-minis is-two">
              <Mini label="Sold" value={v.count.toLocaleString('en-GB')} sub={`${v.online.count || 0} online, ${v.pos.count || 0} at the till`} />
              <Mini label="Money in" value={money(v.revenue)} sub={v.previous ? `${money(v.previous.revenue)} in the period before` : 'Counted in Brought in'} />
              <Mini label="Redeemed" value={money(v.redeemed)} sub="Spent in the salon" />
              <Mini label="Still to spend" value={money(v.outstanding)} sub="On vouchers sold in these dates" />
            </div>
          ) : <Pending>Gift voucher sales couldn’t be read just now.</Pending>}
        </Card>
      </div>
      <p className="pf-rule">Loyalty visits and voucher redemptions are shown here on their own, and never added to the bookings marketing brought in, so nothing is counted twice.</p>
    </div>
  )
}

// ─── The front desk ───
const OUTCOME_LABELS = [['booked', 'Booked'], ['link', 'Link sent'], ['blocked', 'Sent to booking page'], ['message', 'Message taken'], ['transfer', 'Put through'], ['answered', 'Answered']]
export const FrontDeskTab = ({ data, history }) => {
  const { frontDesk, range, prev } = data
  const f = useMemo(() => (frontDesk ? frontDeskResults(frontDesk.conversations, range) : null), [frontDesk, range])
  const before = useMemo(() => (frontDesk && prev && frontDesk.complete ? frontDeskResults(frontDesk.conversations, prev) : null), [frontDesk, prev])
  const go = (path) => { hapticTick(); history.push(path) }
  if (frontDesk === undefined) return <div className="pf-tab"><span className="pf-skel is-chart" /></div>
  if (!frontDesk) return <div className="pf-tab"><Card title="Front desk"><Pending>The front desk couldn’t be read just now.</Pending></Card></div>
  if (!frontDesk.used) {
    return (
      <div className="pf-tab">
        <Card title="Front desk">
          <Empty title="Bailey isn’t answering clients yet" action={<button type="button" className="pf-btn" onClick={() => go('/assistant/front-desk/setup')}>Set up the front desk</button>}>
            Bailey can answer chat on your booking page and your phone, and book clients in. What that brings in shows here.
          </Empty>
        </Card>
      </div>
    )
  }
  return (
    <div className="pf-tab">
      <div className="pf-minis">
        <Mini label="Conversations" value={f.total.toLocaleString('en-GB')} sub={`${plural(f.chats, 'chat', 'chats')}, ${plural(f.calls, 'call', 'calls')}`} />
        <Mini label="Bookings Bailey took" value={f.bookings.toLocaleString('en-GB')} sub={before ? `${plural(before.bookings, 'booking', 'bookings')} in the period before` : 'Booked in the conversation'} />
        <Mini label="Links sent" value={(f.outcomes.link + f.outcomes.blocked).toLocaleString('en-GB')} sub="To book on your booking page" />
        <Mini label="Busiest" value={f.busiest || '·'} sub={f.busiest ? 'Most conversations' : 'Not enough conversations yet'} />
      </div>
      <Card title="What happened" aside={<button type="button" className="pf-link" onClick={() => go('/assistant/front-desk')}>Open the front desk</button>}>
        {f.total ? (
          <div className="pf-outcomes">
            {OUTCOME_LABELS.map(([k, label]) => <div key={k} className={k === 'booked' && f.outcomes[k] ? 'is-good' : ''}><small>{label}</small><b>{f.outcomes[k]}</b></div>)}
          </div>
        ) : <p className="pf-muted">No conversations in these dates.</p>}
        {!frontDesk.complete && <p className="pf-foot">Counted from your most recent conversations.</p>}
      </Card>
    </div>
  )
}
